// src/auditoria/components/ds/vocabulario-kpi.ts
//
// ----------------------------------------------------------------------
// VOCABULÁRIO DOS CARDS DE KPI — A FONTE ÚNICA
//
// Oito lotes escolheram ícone e tom em paralelo e o conferente achou 20 telas
// pintando o MESMO conceito de jeitos diferentes. O mapa abaixo é o desempate:
// agrupa por CONCEITO, nunca por tela, e vale para os 96 cards dos dois apps.
//
// Mora no kit — e não na view do MODELO, onde nasceu — porque um dicionário que
// vive num arquivo de tela não pode ser importado: o portal é outro app e nenhuma
// outra view acha o arquivo. Aqui ele entra pelo barril, do mesmo jeito que o
// `DsKpiCard` que o consome, e a cópia do portal é byte a byte igual a esta.
//
// COMO SE USA: passe `conceito` ao `DsKpiCard` e não escreva `icon`/`tone`.
//
//     <DsKpiCard conceito="dinheiro-a-recuperar" title="ISS a Recuperar" ... />
//
// `icon` e `tone` explícitos continuam vencendo o conceito — é a porta de saída
// para o card cujo tom depende do dado (uma taxa que só é `success` acima da meta).
//
// REGRA DO TOM: o tom diz o que o número SIGNIFICA, não decora.
//   · dinheiro a recuperar nunca é `success`;
//   · soma monetária neutra nunca é `error`;
//   · contagem de base é `primary` (ou `neutral` quando é recorte de catálogo).
//
// REGRA DO DESEMPATE: quando a tela MODELO (`espaco-trabalho-view.tsx`, aprovada
// pelo dono do produto) e a tela de destino discordam, vence o modelo — o usuário
// clica no card de lá e precisa reencontrar o mesmo número com a mesma pintura:
//   · "Ordens a aceitar"     `a-aceitar`  → `/…/ordem-servico/lista`, card
//     "Aguardando Aceite";
//   · "Apurações a concluir" `a-concluir` → `/…/acao-fiscal/index`, card
//     "Em Apuração";
//   · "Malhas a iniciar"     `malhas`     → `/…/malha-fiscal/execucao`. O mesmo par
//     pinta "Malhas Ativas" em Ranking de Malhas e em Análise Fiscal.
// ----------------------------------------------------------------------

import type { DsIconName } from './DsIcon';
import type { DsKpiTone } from './DsKpiCard';

// ----------------------------------------------------------------------

/** Conceitos que os 96 cards contam. Um conceito, uma pintura. */
export type ConceitoKpi =
  // --- dinheiro ---
  /** Crédito a constituir, diferença apurada, receita em risco, valor em atraso. */
  | 'dinheiro-a-recuperar'
  /** Dinheiro que entrou: ISS pago, valor arrecadado. */
  | 'dinheiro-arrecadado'
  /** Soma sem juízo de valor: valor total emitido, valor original, total tomado. */
  | 'dinheiro-neutro'
  /** Débito do contribuinte ainda dentro do prazo (portal). */
  | 'dinheiro-em-aberto'
  /** Uma parcela do crédito, não o total: principal, multa de mora, juros Selic. */
  | 'parcela-de-credito'
  // --- contagens de gente ---
  /** Universo monitorado: base total, contribuintes enquadrados, destinatários. */
  | 'contribuintes-base'
  /** Recorte irregular do universo: inadimplentes, zona crítica. */
  | 'contribuintes-irregulares'
  /**
   * Irregularidade agravada, repetida ou de prioridade máxima: reincidentes,
   * 3+ competências, fuga sistemática, contribuintes acima da faixa de corte.
   */
  | 'contribuintes-reincidentes'
  // --- contagens de papel ---
  /** NFS-e. */
  | 'notas'
  /** Guia de recolhimento — contagem neutra. */
  | 'guias'
  /** Guia emitida e não paga (omissão de pagamento). */
  | 'guias-nao-pagas'
  /** Processo eletrônico / protocolo. */
  | 'processos'
  /** Acordo de parcelamento (a entidade). */
  | 'parcelamentos'
  /** Parcela contratada dentro de um parcelamento. */
  | 'parcelas'
  /** Registro genérico de catálogo ou tabela de apoio. */
  | 'registros'
  /** Recorte de um catálogo (uma faixa do total consolidado). */
  | 'registros-recorte'
  /** Caso/documento em desconformidade: anexo incompatível, enquadramento irregular. */
  | 'documento-irregular'
  /** Ação fiscal (a entidade). */
  | 'acoes-fiscais'
  /** Malha (a entidade) — o MODELO pinta malha com `filtro`. */
  | 'malhas'
  /**
   * Municípios/localidades envolvidos. `entrada-saida` é provisório: falta um
   * glifo de localidade em `DsIconName` (§3.4, pedido no mailbox).
   */
  | 'localidades'
  // --- percentuais: SEMPRE `percentual`, o tom diz se é bom ou ruim ---
  /** Taxa em que subir é bom: regularização, pagamento, conclusão, leitura, metas. */
  | 'percentual-favoravel'
  /** Taxa em que subir é ruim: materialidade, impacto, casos críticos, crescimento. */
  | 'percentual-adverso'
  // --- tempo ---
  /** Duração medida: tempo médio de leitura, tempo médio em dias. */
  | 'tempo-medio'
  /** Fila de espera: em análise, pendentes. */
  | 'aguardando'
  /** Trabalho em curso, sem juízo de valor: situação de processo em andamento. */
  | 'em-andamento'
  /** Cumprindo o prazo: em dia, no prazo. */
  | 'em-dia'
  /** Data futura sem urgência: próximo vencimento. */
  | 'data-vencimento'
  /** Data futura com urgência: vencendo em N dias. */
  | 'vencimento-proximo'
  // --- estados ---
  /** Ocorrência adversa contada: divergências, autos lavrados, alto impacto. */
  | 'ocorrencia-adversa'
  /** Passou do prazo: vencidas, em atraso, prazo vencido. */
  | 'vencido'
  /** Papel conferido e válido: retificadoras, licenças vigentes, apurada, emitidas. */
  | 'documento-regular'
  /** Trabalho fiscal terminado: concluídas, finalizadas. */
  | 'trabalho-concluido'
  /** Vínculo encerrado: parcelamentos encerrados. */
  | 'encerrado'
  /** Cancelado. */
  | 'cancelado'
  // --- caixa de trabalho (o MODELO manda) ---
  /** Item que espera um aceite/aprovação meu. */
  | 'a-aceitar'
  /** Item que espera eu terminar de preencher: apurações a concluir, rascunho. */
  | 'a-concluir'
  /** Item que espera eu acompanhar: ciências, itens a observar. */
  | 'a-acompanhar'
  /** Já lido / já visto. */
  | 'lido'
  /** Enviado. */
  | 'enviado'
  /** Fila de entrada: não lidas, no setor a atribuir. */
  | 'caixa-entrada'
  /** Atribuído a mim. */
  | 'meu';

/**
 * Conceito → (ícone, tom). Uma linha por conceito: se duas telas contam a mesma
 * coisa, elas leem a MESMA linha e não há como divergir de novo.
 */
export const VOCABULARIO_KPI: Record<ConceitoKpi, { icon: DsIconName; tone: DsKpiTone }> = {
  'dinheiro-a-recuperar': { icon: 'cifrao', tone: 'error' },
  'dinheiro-arrecadado': { icon: 'cifrao', tone: 'success' },
  'dinheiro-neutro': { icon: 'cifrao', tone: 'info' },
  'dinheiro-em-aberto': { icon: 'cifrao', tone: 'warning' },
  'parcela-de-credito': { icon: 'cifrao', tone: 'info' },
  'contribuintes-base': { icon: 'grupo', tone: 'primary' },
  'contribuintes-irregulares': { icon: 'grupo', tone: 'warning' },
  'contribuintes-reincidentes': { icon: 'grupo', tone: 'error' },
  notas: { icon: 'nota', tone: 'primary' },
  guias: { icon: 'guia', tone: 'primary' },
  'guias-nao-pagas': { icon: 'guia', tone: 'error' },
  processos: { icon: 'processo', tone: 'primary' },
  parcelamentos: { icon: 'parcelamento', tone: 'primary' },
  parcelas: { icon: 'parcela', tone: 'primary' },
  registros: { icon: 'documento', tone: 'primary' },
  'registros-recorte': { icon: 'documento', tone: 'neutral' },
  'documento-irregular': { icon: 'documento', tone: 'warning' },
  'acoes-fiscais': { icon: 'prancheta', tone: 'primary' },
  malhas: { icon: 'filtro', tone: 'primary' },
  localidades: { icon: 'entrada-saida', tone: 'neutral' },
  'percentual-favoravel': { icon: 'percentual', tone: 'success' },
  'percentual-adverso': { icon: 'percentual', tone: 'warning' },
  'tempo-medio': { icon: 'relogio', tone: 'info' },
  aguardando: { icon: 'relogio', tone: 'warning' },
  'em-andamento': { icon: 'relogio', tone: 'info' },
  'em-dia': { icon: 'relogio', tone: 'success' },
  'data-vencimento': { icon: 'calendario', tone: 'info' },
  'vencimento-proximo': { icon: 'calendario', tone: 'warning' },
  'ocorrencia-adversa': { icon: 'alerta', tone: 'warning' },
  vencido: { icon: 'alerta', tone: 'error' },
  'documento-regular': { icon: 'documento-check', tone: 'success' },
  'trabalho-concluido': { icon: 'trofeu', tone: 'success' },
  encerrado: { icon: 'check-circulo', tone: 'success' },
  cancelado: { icon: 'fechar-circulo', tone: 'error' },
  'a-aceitar': { icon: 'check', tone: 'warning' },
  'a-concluir': { icon: 'editar', tone: 'primary' },
  'a-acompanhar': { icon: 'olho', tone: 'warning' },
  lido: { icon: 'olho', tone: 'success' },
  enviado: { icon: 'enviar', tone: 'primary' },
  'caixa-entrada': { icon: 'caixa-entrada', tone: 'warning' },
  meu: { icon: 'usuario', tone: 'primary' },
};
